import React from 'react';
import { ServiceAttributeSummaryData } from '../../models/PulseData';
import { CustomerInfo } from '../../models/CustomerData';
import './PulsePage.css'; // Assuming common styles
import ReactMarkdown from 'react-markdown'; // Optional: if narrative is markdown
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
// import { OrderPreservingTable } from './OrderPreservingTable';
import { useCustomer } from '../../contexts/CustomerContext';
import SortableTable, { ColumnDefinition } from '../common/SortableTable';

export const getServiceAttributeContentId = (service: string, attribute: string) => 
  `service-attribute-summary-${service}-${attribute}-content-area`;

interface ServiceAttributeSummaryProps {
  data: ServiceAttributeSummaryData;
}


interface ComplexAttributeDetail {
  id: string; // Unique ID for table row key
  code: number | string; // Code might not always be a number
  message: string;
  remarks: string;
  count: number; // Original count for this complex value
}

const ServiceAttributeSummary: React.FC<ServiceAttributeSummaryProps> = ({ data }) => {

  const { selectedCustomer } = useCustomer();

  if (!data) {
    return <p>No service attribute data available.</p>;
  }

  const { services, attribute, counts, narrative, /* summary */} = data;
  const overallTotals: number = counts.reduce((total, item) => total + item.count, 0);

  type CountItem = typeof counts[0];

  const renderProbeLink = (item: CountItem) => {
    // Conditionally render the link based on the customer's feature flags.
    if (!selectedCustomer?.features?.includes('probe') || item.value === "$deriq.other$" || item.value === "") {
      return null;
    }
    // Construct the URL for the probe page with filter parameters
    const probeUrl = `/probe?fa=${encodeURIComponent(attribute)}&fv=${encodeURIComponent(item.value)}&services=${encodeURIComponent(services)}`;

    return (
      <Link to={probeUrl} 
        className="probe-link" 
        title={`Probe for requests with ${attribute} = ${item.value}`}>
        <FontAwesomeIcon icon={faMagnifyingGlass} />
      </Link>
    );
  };  

  const renderValue = (item: CountItem) => {
    if (item.value === "$deriq.other$") {
      return `Others (${attribute})`;
    }
    if (item.value === "") {
      return "(Empty)";
    }

    // For regular values, display the value and the probe link
    return (
      <div className="value-probe-container">
        <span className="value-text">{item.value}</span>
        {renderProbeLink(item)}
      </div>
    );
  };

  const simpleValueColumns: ColumnDefinition<CountItem>[] = [
    { key: 'value', label: attribute, sortable: true, render: renderValue, className: 'first-column', maxLength: 200, },
    { key: 'count', label: 'Count', sortable: true, align: 'right', className: 'total-cell' },
    // { key: 'value', label: 'Actions', sortable: false, render: renderProbeLink, className: 'total-cell' },
  ];

  const simpleCounts: CountItem[] = [];
  const complexDetails: ComplexAttributeDetail[] = [];

  counts.forEach((item, index) => {
    try {
      // Attempt to parse the value as JSON
      const parsedValue = JSON.parse(item.value);
      // Check if it's an array and the first element has the expected structure
      if (Array.isArray(parsedValue) && parsedValue.length > 0 &&
          'code' in parsedValue[0] && 'message' in parsedValue[0]) {
        
        // Assuming we only care about the first object in the array, as per the example
        const detail = parsedValue[0];
        complexDetails.push({
          id: `complex-${attribute}-${index}`, // Generate a unique ID
          code: detail.code,
          message: detail.message,
          remarks: detail.remarks || "no_error_type",
          count: item.count,
        });
      } else {
        // Not the complex structure we're looking for, treat as simple
        simpleCounts.push(item);
      }
    } catch (e) {
      // Not JSON or malformed, treat as simple
      simpleCounts.push(item);
    }

  });

  // ToDo: following structure works only for Tazapay *error_details.
  //  make it extensible for other complex errors to generalize
  const complexDetailColumns: ColumnDefinition<ComplexAttributeDetail>[] = [
    { key: 'code', label: 'Code', sortable: true, className: 'count-cell' }, // Assuming 'code' is like a count cell for width
    { key: 'remarks', label: 'Error Type', sortable: true, className: 'first-column' }, // 'remarks' takes space, so give more width
    { key: 'message', label: 'Message', sortable: true, className: 'first-column' }, // 'message' takes more space, so give more width
    { key: 'count', label: 'Count', sortable: true, align: 'right', className: 'total-cell' },
  ];

  const renderComplexStructure = () => {
    const simpleTableFooter = (
      <tr className="totals-row">
        <td className="first-column">
          <strong>Totals for {attribute}</strong>
        </td>
        <td className="total-cell">
          <strong>{overallTotals}</strong>
        </td>
      </tr>
    );

    const complexTableFooter = (
      <tr className="totals-row">
        <td className="first-column" colSpan={complexDetailColumns.length - 1}>
          <strong>Totals for {attribute}</strong>
        </td>
        <td className="total-cell">
          <strong>{overallTotals}</strong>
        </td>
      </tr>
    );

    return (
      <div className="attribute-summary-table-container">
        {complexDetails.length > 0 && (
          <div className="complex-details-container">
            <h4>Structured Details for {attribute}</h4>
            <SortableTable<ComplexAttributeDetail>
              data={complexDetails}
              columns={complexDetailColumns}
              rowIdKey="id"
              defaultSortConfig={{ key: 'count', direction: 'descending' }}
              footer={simpleCounts.length === 0 ? complexTableFooter : undefined}
            />
          </div>
        )}

        {simpleCounts.length > 0 && (
          <div>
            {complexDetails.length > 0 && <h4>Other Values for {attribute}</h4>} {/* Title if both tables are shown */}
            <SortableTable<CountItem>
              data={simpleCounts}
              columns={simpleValueColumns}
              rowIdKey="value"
              defaultSortConfig={{ key: 'count', direction: 'descending' }}
              footer={simpleTableFooter}
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="service-attribute-item" id={getServiceAttributeContentId(services, attribute)}>
      {/* <h3>Service: {service}</h3> */}
      <h3>Attribute: {attribute}</h3>
      {narrative && (
        <div className="narrative-section">
          {/* <h4>Narrative:</h4> */}
          <ReactMarkdown>{narrative}</ReactMarkdown> {/* Or use <p>{narrative}</p> */}
        </div>
      )}

      {/* v1/before June 4th
        <OrderPreservingTable attribute={attribute} counts={counts} totals={totals} /> 
      */}

      {/* v2/June 4th - plain rendering with SortableTable
        {renderCounts()}
      */}
      
      {/* v2/June 4th - check and render complex structures as well */}
      {renderComplexStructure()}

      {/*  INCLUDE THE SUMMARY LATER ON. for now narrative has info.
      {summary && (
        <div className="attribute-metrics-section">
          <h4>Summary Metrics:</h4>
          <ul>
            {Object.entries(summary).map(([key, value]) => (
              <li key={key}>
                <span className="metric-key">{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:</span> 
                <span className="metric-value">{typeof value === 'number' && value % 1 !== 0 ? value.toFixed(2) : value}</span>
                {key.includes('percentage') && '%'}
              </li>
            ))}
          </ul>
        </div>
      )} */}
    </div>
  );
};


// [{"code":382,"message":"This is an invalid account"."remarks":"accounts_invalid_893"}]
export default ServiceAttributeSummary;